import React, { forwardRef, MouseEventHandler, Ref, useEffect, useState } from 'react';
import { Avatar } from '@salvapp/ui-components';
import { Button, Dropdown } from 'react-bootstrap';
import './AlertRowAssignment.scss';
import { connect, ResolveThunks } from 'react-redux';
import { fetchUpdatedAlert } from './alertActions';
import Has from '../auth/Has';
import { CAN_MODIFY_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER } from '../auth/permissions';
import { DropdownWrapper } from '../common/DropdownWrapper';
import { InternalApiAlertType, InternalApiMonitoringAlert } from '../api-internal';
import { alertAssignmentInternalApi } from '../apis';
import { useMe } from '../organisation/useMe';
import { useAssignableUsers } from '../users/useAssignableUsers';

interface AlertRowAssignmentProps {
  alert: InternalApiMonitoringAlert;
  defaultSize?: number;
}

export const AlertRowAssignment = ({ alert, defaultSize = 40, fetchUpdatedAlert }: ComponentProps) => {
  interface ToggleProps {
    onClick: MouseEventHandler;
  }
  const { assignableUsers } = useAssignableUsers();
  const [displayedAssignments, setDisplayedAssignments] = useState(assignableUsers?.slice(0, defaultSize));
  const { id } = useMe();

  const currentUser = assignableUsers?.find((user) => user.id === id);

  useEffect(() => {
    setDisplayedAssignments(assignableUsers?.slice(0, defaultSize));
  }, [assignableUsers, defaultSize]);

  const Toggle = forwardRef(({ onClick }: ToggleProps, ref: Ref<HTMLButtonElement>) => {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      onClick(e);
    };

    return (
      <Has anyPermissions={[CAN_MODIFY_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
        {(can: boolean) =>
          can ? (
            (alert.assignee && (
              <Button
                size='sm'
                variant='outline-secondary'
                className='d-flex flex-row align-items-center dropdown-toggle'
                ref={ref}
                onClick={handleClick}
              >
                <Avatar email={alert.assignee.email} />
                <span className='ms-2'>{alert.assignee.name}</span>
              </Button>
            )) || (
              <Button ref={ref} onClick={handleClick} size='sm' className='btn-white dropdown dropdown-toggle'>
                Assign
              </Button>
            )
          ) : (
            <div>
              {alert.assignee && (
                <div>
                  <Avatar email={alert.assignee.email} />
                  <span className='ms-2'>{alert.assignee.name}</span>
                </div>
              )}
            </div>
          )
        }
      </Has>
    );
  });
  Toggle.displayName = 'Toggle';

  const handleClick = async (userId?: number) => {
    if (userId) {
      await alertAssignmentInternalApi.assignAlert({
        internalApiAlertAssignmentCommand: {
          alertId: alert.id,
          alertType: InternalApiAlertType.MONITORING,
          assigneeId: userId,
        },
      });
    } else {
      await alertAssignmentInternalApi.unassignAlert({
        internalApiAlertAssignmentCommand: { alertId: alert.id, alertType: InternalApiAlertType.MONITORING },
      });
    }
    fetchUpdatedAlert(alert.id);
  };

  const handleAssignToMe = async () => {
    if (currentUser) handleClick(currentUser.id);
  };

  const searchUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setDisplayedAssignments(
      assignableUsers?.filter((user) => {
        return (
          user.email && (user.name.toLocaleLowerCase().includes(query) || user.email.toLowerCase().includes(query))
        );
      })
    );
  };

  return (
    <DropdownWrapper className='assignee'>
      <Dropdown.Toggle as={Toggle} />
      <Dropdown.Menu data-testid='assignee-menu' align={{ sm: 'start' }}>
        <div className='row px-4'>
          <div className='col dropdown-search-assignee'>
            <input
              type='text'
              className='form-control form-control-sm'
              placeholder='Search ....'
              onChange={(e) => searchUsers(e)}
              autoFocus
            />
          </div>
          {currentUser && (
            <Dropdown.Item data-testid='dropdown-item' className='col assign-self-button'>
              <Button size='sm' variant='outline-primary' onClick={handleAssignToMe}>
                Assign to me
              </Button>
            </Dropdown.Item>
          )}
        </div>
        <Dropdown.Divider />
        <div className='items'>
          {displayedAssignments?.map((user) => (
            <Dropdown.Item
              data-testid='dropdown-item'
              key={user.id}
              as='button'
              className='d-flex flex-row align-items-center dropdown-item-regular'
              onClick={() => handleClick(user.id)}
            >
              <Avatar email={user.email} />
              <div className='d-flex flex-column'>
                <div className='ms-2'>{user.name}</div>
                <div className='text-muted ms-2 small'>{user.email}</div>
              </div>
            </Dropdown.Item>
          ))}
        </div>
      </Dropdown.Menu>
    </DropdownWrapper>
  );
};

const mapDispatchToProps = {
  fetchUpdatedAlert: fetchUpdatedAlert,
};

type ComponentProps = ResolveThunks<typeof mapDispatchToProps> & AlertRowAssignmentProps;

export default connect(null, mapDispatchToProps)(AlertRowAssignment);
